import asyncSeries from 'async/series';
import React from 'react';
import { withRouter } from 'inferno-router';
import {
  StyleSheet,
  View,
  Text,
  TextInput,
  ScrollView,
} from './components/base_components';
import { BusyComponent, BusyOverlay } from './components/busy_component';
import ProgressSpinner from './components/progress_spinner';
import TextButton from './components/buttons/text_button';
import UserSelect from './components/user_select';
import Checkbox from './components/checkbox';
import UserAdd from './components/user_add';

import UserAdminStore from './stores/user_admin_store';
import UserStore from './stores/user_store';
import VolumeStore from './stores/volume_store';

const VOLUME_REGEX = /^[A-Za-z_0-9]*$/;

const styles = StyleSheet.create({
  orgEditScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  scroller: {
    flex: 1,
    alignSelf: 'stretch',
    flexDirection: 'column',
  },
  scrollInner: {
    paddingLeft: '3em',
    paddingRight: '3em',
    paddingTop: '3em',
    paddingBottom: '5em',
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    overflowY: 'scroll',
  },
  line: {
    alignSelf: 'stretch',
    height: '1px',
    backgroundColor: 'black',
  },
  headerText: {
    color: '#222',
    fontSize: '1.2em',
    fontWeight: 'bold',
  },
  list: {
    marginTop: '1em',
    marginBottom: '1em',
  },
  add: {
    marginTop: '1em',
  },
  text: {
    marginTop: '1em',
    color: '#222',
    fontSize: '1.2em',
    fontWeight: 'bold',
  },
  itemLine: {
    marginTop: '0.5em',
    marginBottom: '0.5em',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  value: {
    width: '20em',
  },
  itemButton: {
    marginRight: '2em',
    height: '2em',
  },
  addVolume: {
    marginTop: '0.5em',
    marginBottom: '0.5em',
  },
  button: {
    marginTop: '1em',
    marginBottom: '1em',
    height: '3em',
  },
  orgSelect: {
    marginBottom: '0.5em',
    marginTop: '0.5em',
  },
  input: {
    marginTop: '0.5em',
    width: '25em',
    height: '3em',
    paddingLeft: '1em',
    color: 'black',
    backgroundColor: '#ccc',
    borderRadius: '0.5em',
  },
  label: {
    marginTop: '0.5em',
  },
  checkbox: {
    marginTop: '1em',
  },
  spacer: {
    height: '1em',
  },
});

type Props = {
  match: any;
};
type State = {
  org_id: number;
  org_name: string;
  volume_list: [any] | null;
  user_list: [any] | null;
  add_user_id: string;
  volume_name: string;
  is_media_volume: boolean;
};

class OrgEditScreen extends BusyComponent<Props, State> {
  constructor(props, context) {
    super(props, context);
    const org_id = parseInt(this.props.match.params.org_id);
    const org_list = UserStore.getUser().org_list;
    const org = org_list.find((o) => o.org_id === org_id);
    const org_name = (org && org.org_name) || String(org_id);
    this.state = {
      org_id,
      org_name,
      volume_list: null,
      user_list: null,
      add_user_id: '',
      volume_name: '',
      is_media_volume: true,
    };
  }
  componentDidMount() {
    UserAdminStore.addChangeListener(this._onUserUpdate);
    VolumeStore.addChangeListener(this._onVolumeUpdate);
    UserAdminStore.fetch();
    VolumeStore.fetch();
    this._onUserUpdate();
    this._onVolumeUpdate();
  }
  componentWillUnmount() {
    UserAdminStore.removeChangeListener(this._onUserUpdate);
    VolumeStore.removeChangeListener(this._onVolumeUpdate);
  }
  _onUserUpdate = () => {
    const list = UserAdminStore.getList();
    const { org_id } = this.state;
    if (list) {
      const user_list = list.filter((user) => {
        return user.org_list.indexOf(org_id) !== -1;
      });
      this.setState({ user_list });
    }
  };
  _onVolumeUpdate = () => {
    const list = VolumeStore.getList();
    if (list) {
      const { org_id } = this.state;
      const volume_list = list
        .filter((v) => v.org_id === org_id)
        .sort(_sortVolume);
      this.setState({ volume_list });
    }
  };
  _onUserRemove = (user) => {
    const { org_id } = this.state;
    if (this.setBusy()) {
      const opts = { org_id, user_id: user.user_id };
      UserAdminStore.deleteOrg(opts, (err) => {
        this.clearBusy();
        if (err) {
          window.alert('Failed to remove user from org');
        }
      });
    }
  };

  _onAddUser = () => {
    const { org_id, add_user_id } = this.state;
    if (add_user_id && this.setBusy()) {
      const opts = { org_id, user_id: add_user_id };
      UserAdminStore.addOrg(opts, (err) => {
        this.clearBusy();
        if (err) {
          window.alert('Failed to add user to org');
        }
      });
    }
  };
  _onAddVolume = () => {
    const { org_id, volume_name, is_media_volume } = this.state;
    if (volume_name) {
      if (!VOLUME_REGEX.test(volume_name)) {
        window.alert(
          'Bad volume name, A-Z, a-z, 0-9, _ are the only allowed characters.'
        );
      } else if (this.setBusy()) {
        const opts = { org_id, volume_name, is_media_volume };
        VolumeStore.createVolume(opts, (err) => {
          this.clearBusy();
          if (err) {
            window.alert('Failed to create volume, err: ' + err);
          }
        });
      }
    }
  };
  _onUserManage = (user) => {
    const { org_id } = this.state;
    this.props.history.push(`/admin/org/${org_id}/user/${user.user_id}`);
  };
  _onVolumeManage = (volume) => {
    this.props.history.push('/admin/volume/' + volume.volume_id);
  };
  _onNewUser = (user) => {
    if (this.setBusy()) {
      const { org_id } = this.state;
      _newUser({ org_id, user }, (err) => {
        this.clearBusy();
        if (err === 409) {
          window.alert('User already exists');
        } else if (err) {
          window.alert('Failed to add user.');
        }
      });
    }
  };

  render() {
    const {
      org_name,
      user_list,
      volume_list,
      volume_name,
      add_user_id,
      is_media_volume,
    } = this.state;

    let users = null;
    if (user_list) {
      users = user_list.map((user, i) => (
        <User
          key={i}
          user={user}
          onManage={this._onUserManage}
          onDelete={this._onUserRemove}
        />
      ));
    } else {
      users = <ProgressSpinner />;
    }

    let volumes = null;
    if (volume_list) {
      volumes = volume_list.map((volume, i) => (
        <Volume key={i} volume={volume} onManage={this._onVolumeManage} />
      ));
    } else {
      volumes = <ProgressSpinner />;
    }

    return (
      <View style={styles.orgEditScreen}>
        <ScrollView
          style={styles.scroller}
          contentContainerStyle={styles.scrollInner}
        >
          <Text style={styles.headerText}>MONP ABT</Text>
          <Line />
          <Text style={styles.text}>Org: {org_name}</Text>
          <Line />
          <Text style={styles.text}>Users:</Text>
          {users}
          <Line />
          <Text style={styles.text}>Add New User To Org:</Text>
          <UserAdd style={styles.add} onAdd={this._onNewUser} />
          <View style={styles.spacer} />
          <Line />
          <Text style={styles.text}>Add Existing User To Org:</Text>
          <UserSelect
            style={styles.addUser}
            excludeList={user_list}
            value={add_user_id}
            onChange={(add_user_id) => this.setState({ add_user_id })}
          />
          <TextButton
            style={styles.button}
            text='Add User'
            onPress={this._onAddUser}
          />
          <Line />
          <Text style={styles.text}>Volumes:</Text>
          {volumes}
          <Text style={styles.text}>Add Volume:</Text>
          <TextInput
            style={styles.input}
            placeholder='Volume Name'
            value={volume_name}
            onChangeText={(volume_name) => this.setState({ volume_name })}
          />
          <Checkbox
            style={styles.checkbox}
            text='Media Volume'
            value={is_media_volume}
            onChange={(is_media_volume) => this.setState({ is_media_volume })}
          />
          <TextButton
            style={styles.button}
            text='Add Volume'
            onPress={this._onAddVolume}
          />
          <Line />
        </ScrollView>
        <BusyOverlay isBusy={this.state.is_busy} />
      </View>
    );
  }
}
export default withRouter(OrgEditScreen);

function Line() {
  return <View style={styles.line} />;
}
function User(props) {
  const { user, onManage, onDelete } = props;
  const { user_name } = user;
  function _onDelete() {
    onDelete(user);
  }
  function _onManagePress() {
    onManage(user);
  }
  return (
    <View style={styles.itemLine}>
      <Text style={styles.value}>{user_name}</Text>
      <TextButton
        style={styles.itemButton}
        text={'Manage'}
        onPress={_onManagePress}
      />
      <TextButton
        style={styles.itemButton}
        text={'Remove'}
        onPress={_onDelete}
      />
    </View>
  );
}
function Volume(props) {
  const { volume, onManage } = props;
  const { volume_name, is_disabled } = volume;
  function _onManagePress() {
    onManage(volume);
  }
  let value = volume_name;
  if (is_disabled) {
    value += ' (disabled)';
  }
  return (
    <View style={styles.itemLine}>
      <Text style={styles.value}>{value}</Text>
      <TextButton
        style={styles.itemButton}
        text={'Manage'}
        onPress={_onManagePress}
      />
    </View>
  );
}

function _newUser(params, done) {
  const { user, org_id } = params;
  let user_id;
  asyncSeries(
    [
      (done) => {
        UserAdminStore.addUser(user, (err, new_user) => {
          if (!err) {
            user_id = new_user.user_id;
          }
          done(err);
        });
      },
      (done) => {
        const opts = { org_id, user_id };
        UserAdminStore.addOrg(opts, done);
      },
    ],
    done
  );
}
function _sortVolume(a, b) {
  if (a.is_disabled && !b.is_disabled) {
    return 1;
  } else if (b.is_disabled && !a.is_disabled) {
    return -1;
  } else {
    return a.volume_name.localeCompare(b.volume_name, undefined, {
      sensitivity: 'base',
    });
  }
}
